'use client'

import { useState, FormEvent } from 'react'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!email) return
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/auth/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json() as { success?: boolean; error?: string }
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}>
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)' }}
        />
      </div>

      <div className="relative w-full max-w-sm mx-4">
        {/* Wordmark */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5">
            <div className="flex items-center gap-2.5">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 2 100 112" width="36" height="36">
                <defs>
                  <linearGradient id="studio-login-eg" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#10b981"/><stop offset="100%" stopColor="#059669"/>
                  </linearGradient>
                </defs>
                <path d="M 63 13 A 46 46 0 1 0 63 103" fill="none" stroke="#ffffff" strokeWidth="12" strokeLinecap="round"/>
                <line x1="63" y1="66" x2="90" y2="66" stroke="url(#studio-login-eg)" strokeWidth="12" strokeLinecap="round"/>
                <line x1="63" y1="66" x2="63" y2="103" stroke="#ffffff" strokeWidth="12" strokeLinecap="round"/>
                <circle cx="63" cy="38" r="7" fill="url(#studio-login-eg)"/>
              </svg>
              <div className="flex items-baseline">
                <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5, color: '#10b981' }}>In</span>
                <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5, color: '#f8fafc' }}>Gen</span>
                <span style={{ fontSize: 22, fontWeight: 300, letterSpacing: 0.5, color: '#94a3b8', marginLeft: 4 }}>Studio</span>
              </div>
            </div>
          </div>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
            {sent ? '' : 'Sign in to your account'}
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-8" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          {sent ? (
            <div className="text-center space-y-3">
              <div className="text-3xl">📬</div>
              <h2 className="text-white font-semibold text-lg">Check your email</h2>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                We sent a magic link to{' '}
                <span className="text-white font-medium">{email}</span>.
                Click it to sign in.
              </p>
              <button
                onClick={() => { setSent(false); setEmail('') }}
                className="text-xs mt-4 transition-colors"
                style={{ color: 'var(--accent)' }}
              >
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={e => void handleSubmit(e)} className="space-y-5" noValidate>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#94a3b8' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl text-sm text-white transition-all"
                  style={{
                    background: 'var(--surface-2)',
                    border: 'none',
                    outline: 'none',
                    boxShadow: '0 0 0 1px var(--surface-2)',
                  }}
                  onFocus={e => { e.target.style.boxShadow = '0 0 0 1px #10b981' }}
                  onBlur={e => { e.target.style.boxShadow = '0 0 0 1px var(--surface-2)' }}
                  placeholder="you@yourbusiness.com"
                />
              </div>

              {error && (
                <div className="text-sm px-4 py-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email}
                className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-all"
                style={{
                  background: loading || !email ? 'var(--accent-dark)' : 'linear-gradient(135deg, var(--accent) 0%, var(--teal) 100%)',
                  opacity: loading || !email ? 0.7 : 1,
                }}
              >
                {loading ? 'Sending…' : 'Send Magic Link'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs mt-6" style={{ color: 'var(--muted)' }}>
          Powered by InGen Intelligence
        </p>
      </div>
    </div>
  )
}
